<script lang="ts" setup>
import QrcodeVue from 'qrcode.vue';
import {onMounted, ref, Ref, watch} from 'vue';
import {VueRecaptcha} from 'vue-recaptcha';
import {analyticsInit, trackEvent} from '../../components/app/analytics';
import {AccountSettingsService} from '../../components/settings/account-settings/account-settings.service';
import {changeLanguage, getStrings} from '../../core/lang';
import {useService} from '../../core/useService';
import {ChangePasswordRecord} from '../../records/change-password.record';
import {LoginRecord} from '../../records/login.record';
import {UISettingsRecord} from '../../records/ui-settings.record';
import {AuthResponse, AuthService} from '../auth.service';
import strings from '../auth.strings.yaml';
import OneTimePassword from './OneTimePassword.vue';
import MXTour from '../../components/tour/MXTour.vue';

const S = ref<AuthStrings>(getStrings<AuthStrings>(strings));
const authService = useService<AuthService>(AuthService);

const props = defineProps<{
    account: string;
}>();
const emit = defineEmits(['success', 'ready']);

enum LoginAction {
    Idle,
    Login,
    Enroll2FA,
    Enroll2FAComplete,
    Request2FA,
    ForgotPassword,
    ForgotPasswordSent,
    ResetPassword,
    Using2FA
}
const showLogin: Ref<boolean> = ref(false);
const action: Ref<LoginAction> = ref();
const loginRecord: Ref<LoginRecord> = ref(new LoginRecord());
const passwordRecord: Ref<ChangePasswordRecord> = ref(null);
const authResponse: Ref<AuthResponse> = ref(null);
const secret: Ref<string> = ref(null);
const loading: Ref<boolean> = ref(false);
const uiSettings = ref<UISettingsRecord>(null);

loginRecord.value.account = props.account;

// load ui settings
authService.uiSettings(loginRecord.value.account).then((settings: UISettingsRecord) => {
    analyticsInit(settings);
    uiSettings.value = settings;
    changeLanguage(settings.language);
    changeTitle(settings.title);
    S.value = getStrings<AuthStrings>(strings);
});

function changeTitle(bkVersion: string) {
    if (!IS_DEBUG) {
        document.title = `${bkVersion} [ui-version: ${window.ASSETS_URL.replace(
            /https:\/\/assets\.maxoptra\.com\/(.+?)\/$/,
            '$1'
        )}]`;
    }
}

onMounted(() => {
    checkLogin().then(() => {
        // ready.value = true;
        emit('ready', showLogin.value);
    });
});

watch(
    () => loginRecord.value.code,
    async (code) => {
        // If the user has entered an OTP
        if (code.length === 6) {
            if (action.value === LoginAction.Enroll2FA) {
                // if it is the first time setting up 2FA
                loading.value = true;
                authResponse.value = await authService.activate2FA(code);
                if (!authResponse.value.errors?.length) {
                    // // if code was correct
                    // await checkLogin();
                    // loginRecord.value.code = '';
                    action.value = LoginAction.Enroll2FAComplete;
                    const settings = await useService(AccountSettingsService).getAppSettings();
                    settings.twoFactorAuthActive = true;
                    settings.twoFactorAuthEnabled = true;
                }
                loading.value = false;
            } else if (action.value === LoginAction.Using2FA) {
                // if 2fa is enabled for this user then just login
                await login();
            }
        }
    }
);

/**
 * Check if the user is already logged in and redirect to default page if they are
 */
async function checkLogin() {
    const hash = document.location.hash.split('hash=')[1];
    // Reset Password
    if (hash) {
        passwordRecord.value = new ChangePasswordRecord();
        passwordRecord.value.accountUrl = props.account;
        passwordRecord.value.hash = hash;
        action.value = LoginAction.ResetPassword;
        await authService.getLoginByLink(hash).then((response) => {
            if (response?.errors && response?.errors.length) {
                authResponse.value = response;
            } else {
                passwordRecord.value.login = response.rows[0];
                authResponse.value = null;
            }
        });
        showLogin.value = true;
    } else {
        const loggedIn = await authService.isLoggedIn();
        if (loggedIn) {
            loading.value = true;
            const {twoFactorAuthActive, twoFactorAuthEnabled} =
                await useService(AccountSettingsService).getAppSettings();
            if (twoFactorAuthActive && !twoFactorAuthEnabled && !sessionStorage.getItem('skip2FA')) {
                // if 2fa is active for account and not enabled for this user
                action.value = LoginAction.Request2FA;
                loading.value = false;
                showLogin.value = true;
            } else {
                // if 2fa is not active for account
                emit('success');
                console.log('success', showLogin.value);
                showLogin.value = false;
            }
            if (twoFactorAuthActive && twoFactorAuthEnabled) {
                trackEvent('action', 'login-2fa', null);
            } else {
                trackEvent('action', 'login', null);
            }
            authService.keepAlive();
        } else {
            action.value = LoginAction.Login;
            showLogin.value = true;
        }
    }
}

/**
 * Setting up 2FA
 */
async function setup2FA() {
    // Request a secret from the server
    loading.value = true;
    secret.value = await authService.get2FASecret();
    loading.value = false;
    action.value = LoginAction.Enroll2FA;
}

function skip2FA() {
    emit('success');
    showLogin.value = false;
    sessionStorage.setItem('skip2FA', '1');
    trackEvent('action', '2fa-enrol-skip', null);
}

/**
 * Check that login and password are valid and captcha is valid if required
 */
function isLoginAndPasswordValid(): boolean {
    return (
        loginRecord.value.login?.length > 0 &&
        loginRecord.value.password?.length > 0 &&
        (!authResponse.value?.showCaptcha || loginRecord.value.googleRecaptchaResponse?.length > 0)
    );
}

/**
 * Login the user
 */
async function login() {
    if (isLoginAndPasswordValid()) {
        loading.value = true;
        authResponse.value = await authService.login(loginRecord.value);
        if (authResponse.value?.errors?.length) {
            if (action.value === LoginAction.Using2FA) {
                loginRecord.value.code = '';
                // action.value = LoginAction.Login;
            }
            loading.value = false;
        } else if (authResponse.value?.required2FA) {
            // if 2fa is required for this user
            action.value = LoginAction.Using2FA;
            loading.value = false;
        } else {
            // if 2fa is not required for this user
            await checkLogin();
            // loading.value = false;
        }
    }
}

/**
 * Reset the password
 */
async function resetPassword() {
    if (loginRecord.value.login?.length > 0) {
        // send the email
        loading.value = true;
        authResponse.value = await authService.rememberPassword(loginRecord.value);
        loading.value = false;
        if (!authResponse.value.errors?.length) {
            // if no errors, show the success message
            action.value = LoginAction.ForgotPasswordSent;
            trackEvent('action', 'forgot-password', null);
        }
    }
}
async function changePassword() {
    authResponse.value = await authService.resetPassword(passwordRecord.value).then((result) => {
        if (result?.success) {
            emit('success');
            showLogin.value = false;
            trackEvent('action', 'password-change', null);
        } else {
            return result;
        }
    });
}

function reload() {
    window.location.reload();
}
/**
 * Recaptcha callback
 */
function onVerify(recaptchaToken: string) {
    loginRecord.value.googleRecaptchaResponse = recaptchaToken;
}

/**
 * Format 2FA secret to be displayed
 */
function format2FASecret(secret: string): string {
    return secret.replace(/(.{4})/g, '$1 ').trim();
}
</script>
<template>
    <div v-if="showLogin">
        <MXTour context="login-component" :disabled="false" v-show="showLogin" />
        <!-- Loader -->
        <div class="loader" v-if="loading">
            <div class="spinner"></div>
            <div class="loader-text">{{ S.pleaseWait }}</div>
        </div>

        <!-- Login -->

        <div class="p-2" v-if="action == LoginAction.Login">
            <h1 class="text-3xl font-medium mb-5">{{ S.welcomeBack }}</h1>
            <input
                v-model="loginRecord.login"
                autofocus
                class="block"
                type="text"
                :placeholder="S.loginPlaceholder"
                v-test-id="'login'"
            />
            <input
                @keyup.enter="login"
                v-model="loginRecord.password"
                class="block"
                type="password"
                :placeholder="S.passwordPlaceholder"
                v-test-id="'password'"
            />
            <span @click="action = LoginAction.ForgotPassword" class="link" v-test-id="'forgot-password'">
                {{ S.forgot }}
            </span>
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span class="alert block" v-for="error in authResponse.errors" v-test-id="'login-error-message'">
                    {{ S[error.message] }}
                </span>
            </div>
            <button @click="login" class="block mt-10" v-test-id="'login-btn'">
                {{ S.logIn }}
            </button>
            <div class="my-4 block" v-if="authResponse?.showCaptcha && uiSettings?.rcSiteKey">
                <VueRecaptcha @verify="onVerify" :sitekey="uiSettings.rcSiteKey" v-test-id="'recaptcha'" />
            </div>
        </div>

        <!-- Request 2FA -->

        <div class="p-2" v-else-if="action == LoginAction.Request2FA">
            <h1 class="text-3xl font-medium mb-5">{{ S.twoFactorAuth }}</h1>
            <p class="mb-5">{{ S.twoFactorAuthDescription }}</p>
            <div class="flex">
                <button @click="skip2FA" class="mr-4 inverted block" v-test-id="'skip-2fa'">{{ S.skip }}</button>
                <button @click="setup2FA" class="block" v-test-id="'set-2fa'">{{ S.setItUp }}</button>
            </div>
        </div>

        <!-- Enroll2FA 2FA -->

        <div class="p-2" v-else-if="action == LoginAction.Enroll2FA">
            <h1 class="text-3xl font-medium mb-5">{{ S.setupTwoFactorAuth }}</h1>
            <ol class="list-decimal ml-4">
                <li class="list-item mb-4" v-html="S.instructionLine1"></li>
                <li class="list-item mb-4" v-html="S.instructionLine2"></li>
                <li class="list-item mb-4" v-html="S.instructionLine3"></li>
                <QrcodeVue
                    :value="`otpauth://totp/Maxoptra:${loginRecord.login}@${loginRecord.account}?secret=${secret}&issuer=Maxoptra`"
                    :size="150"
                />
                <p class="my-4 text-lg font-semibold w-52 font-mono" v-test-id="'2fa-secret'">
                    {{ format2FASecret(secret) }}
                </p>
                <li class="list-item mb-4" v-html="S.instructionLine4"></li>
            </ol>
            <OneTimePassword :count="6" v-model:value="loginRecord.code" />
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span class="alert block" v-for="error in authResponse.errors" v-test-id="'enroll-error-message'">
                    {{ S[error.message] }}
                </span>
            </div>
            <button
                @click="
                    authResponse = null;
                    action = LoginAction.Request2FA;
                "
                v-test-id="'login-back'"
                class="mt-8 inverted"
            >
                {{ S.back }}
            </button>
        </div>

        <!-- Enroll 2FA complete -->

        <div class="p-2" v-else-if="action == LoginAction.Enroll2FAComplete">
            <h1 class="text-3xl font-medium mb-5">{{ S.congrats }}</h1>
            <p class="mb-5">{{ S.twoFactorAuthEnabled }}</p>
            <button @click="checkLogin()" class="mr-auto ml-auto block !w-auto" v-test-id="'2fa-continue'">
                {{ S.continue }}
            </button>
        </div>

        <!-- Using 2FA -->

        <div class="p-2" v-else-if="action == LoginAction.Using2FA">
            <h1 class="text-3xl font-medium mb-5">{{ S.twoFactorAuth }}</h1>
            <p class="mb-5 text-dark">{{ S.instructionLine4 }}</p>
            <OneTimePassword :count="6" v-model:value="loginRecord.code" />
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span class="alert block" v-for="error in authResponse.errors" v-test-id="'2fa-error-message'">
                    {{ S[error.message] }}
                </span>
                <span class="" v-html="S.support"></span>
            </div>
            <div>
                <label>
                    <input
                        v-model="loginRecord.trustBrowser"
                        type="checkbox"
                        class="mt-5 pl-2 w-6"
                        v-test-id="'trust-browser'"
                    />
                    <span>{{ S.rememberThisBrowser }}</span>
                </label>
            </div>
        </div>

        <!--Forgot password  -->

        <div class="p-2" v-else-if="action == LoginAction.ForgotPassword">
            <h1 class="text-3xl font-medium mb-5">{{ S.passwordSent }}</h1>
            <p class="mb-5 text-dark">
                {{ S.forgotPasswordMessage }}
            </p>
            <input
                autofocus
                v-model="loginRecord.login"
                class="block"
                type="text"
                :placeholder="S.loginPlaceholder"
                v-test-id="'forgot-password-login'"
            />
            <div class="my-4 block" v-if="authResponse?.errors?.length">
                <span
                    class="alert block"
                    v-for="error in authResponse.errors"
                    v-test-id="'forgot-password-error-message'"
                >
                    {{ S[error.message] }}
                </span>
            </div>
            <div class="flex">
                <button
                    @click="
                        authResponse = null;
                        action = LoginAction.Login;
                    "
                    class="mr-4 inverted block"
                    v-test-id="'forgot-password-back'"
                >
                    {{ S.back }}
                </button>
                <button
                    :class="{disabled: !loginRecord.login?.length}"
                    @click="resetPassword"
                    class="block"
                    v-test-id="'reset-password'"
                >
                    {{ S.resetPassword }}
                </button>
            </div>
        </div>

        <!--Forgot password sent -->

        <div class="p-2" v-else-if="action == LoginAction.ForgotPasswordSent">
            <h1 class="text-3xl font-medium mb-5">{{ S.passwordSent }}</h1>
            <p class="mb-5 text-dark">{{ S.passwordSentMessage }}</p>
            <button
                @click="
                    authResponse = null;
                    action = LoginAction.Login;
                "
                class="mr-4"
                v-test-id="'password-sent-back'"
            >
                {{ S.back }}
            </button>
        </div>

        <!-- Reset password TODO -->

        <div class="p-2" v-else-if="action == LoginAction.ResetPassword">
            <div v-if="passwordRecord.login">
                <span>
                    {{ S.changePassword }}
                    <strong>{{ passwordRecord.login }}</strong>
                </span>
                <input
                    v-model="passwordRecord.newPassword"
                    autofocus
                    class="block"
                    type="password"
                    :placeholder="S.newPassword"
                    v-test-id="'new-password'"
                />
                <input
                    v-model="passwordRecord.repeatNewPassword"
                    class="block"
                    type="password"
                    :placeholder="S.newPasswordConfirm"
                    v-test-id="'new-password-repeat'"
                />
                <span class="my-3.5 inline-block text-dark">{{ S.passwordRule }}</span>
                <div class="mb-4 block" v-if="authResponse?.errors?.length">
                    <span
                        class="alert block"
                        v-for="error in authResponse.errors"
                        v-test-id="'new-password-error-message'"
                    >
                        {{ S[error.message] }}
                    </span>
                </div>
                <button
                    @click="changePassword"
                    class="block mt-10"
                    :class="{
                        disabled:
                            !passwordRecord?.newPassword?.length ||
                            !passwordRecord?.repeatNewPassword?.length ||
                            passwordRecord?.repeatNewPassword != passwordRecord?.newPassword
                    }"
                    v-test-id="'change-password'"
                >
                    {{ S.changeAndLogin }}
                </button>
            </div>

            <!-- if reset password link is invalid -->
            <div v-if="!passwordRecord.login && authResponse?.errors?.length">
                <h3>{{ S.linkIsExpired }}</h3>
                <h4>
                    <a @click="reload" v-test-id="'reload'">
                        {{ S.linkToLogIn }}
                    </a>
                </h4>
            </div>
        </div>
    </div>
</template>
<style lang="less" scoped>
// styles  scoped to this component

p,
li,
span {
    font-size: 16px;
}

.disabled {
    opacity: 0.5;
    pointer-events: none;
}

.link {
    color: #0073ff;
    cursor: pointer;
    margin: 19px 0 10px 0;
    display: inline-block;
    font-weight: 600;
    &:hover {
        text-decoration: underline;
    }
}

.alert {
    background-color: #fdf1f5;
    color: #e47398;
    height: fit-content;
    border-radius: 4px;
    line-height: 30px;
    padding: 5px 15px;
}

.loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.97);

    .spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: 5px solid #1b1c41;
        border-top: 5px solid #fff;
        animation: spin 2s linear infinite;
    }
    .loader-text {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, 50px);
        font-size: 16px;
        font-weight: 600;
        color: #1b1c41;
        opacity: 0.6;
        font-weight: 500;
        width: 100%;
        text-align: center;
    }
}

@keyframes spin {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}

input[type='text'],
input[type='password'],
button {
    height: 44px;
    padding: 8px 12px;
    font-size: 16px;
    border-radius: 4px;
    line-height: 12px;
    appearance: menulist-button;
    background-image: none !important;
    margin-top: 10px;
}
.block {
    display: block;
    width: 100%;
    box-sizing: border-box;
}
input[type='text'],
input[type='password'] {
    border: 2px solid #d5d6de;
    appearance: menulist-button;
    background-image: none !important;
    color: fieldtext !important;
    height: 64px;
}
input[type='text']:focus,
input[type='password']:focus {
    border: 2px solid #1b1c41;
}
button {
    background-color: #1b1c41;
    color: #fff;
    &:active {
        border-color: #d5d6de;
        background-color: lighten(#1b1c41, 5%);
    }
}
button.inverted {
    background-color: #fff;
    color: #1b1c41;
    border: 1px solid #1b1c41;
    &:active {
        background-color: darken(#fff, 5%);
    }
}
.digit {
    width: 28px;
    padding: 0 4px;
    font-size: 28px;
    margin-right: 4px;
}

.text-dark {
    color: rgb(27, 28, 65);
    line-height: 25px;
}
</style>
